import React, { useEffect, useRef } from 'react';
import './Hero.css';

const Hero = () => {
  const appName = process.env.REACT_APP_NAME || 'UltraCoder IDE';
  const appVersion = process.env.REACT_APP_VERSION || '2.0.0';
  const appDescription = process.env.REACT_APP_DESCRIPTION || 'The AI-Powered Intelligent Development Environment';
  const docsUrl = process.env.REACT_APP_DOCS_URL || '#';
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    class Particle {
      constructor() {
        this.reset();
      }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.4;
        this.speedY = (Math.random() - 0.5) * 0.4;
        this.opacity = Math.random() * 0.5 + 0.1;
        this.color = Math.random() > 0.5 ? '99,102,241' : '139,92,246';
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) {
          this.reset();
        }
      }
      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity;
        ctx.fillStyle = `rgb(${this.color})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    for (let i = 0; i < 80; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      // Draw connections
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.save();
            ctx.globalAlpha = (1 - dist / 100) * 0.15;
            ctx.strokeStyle = '#6366f1';
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
            ctx.restore();
          }
        });
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero" id="hero">
      <canvas ref={canvasRef} className="hero__canvas" />

      <div className="hero__content">
        <div className="hero__badge">
          <span className="hero__badge-dot"></span>
          Version {appVersion} — Now Available
        </div>

        <h1 className="hero__title">
          The Future of <br />
          <span className="hero__title-gradient">Code Intelligence</span>
        </h1>

        <p className="hero__subtitle">{appDescription}</p>

        <p className="hero__description">
          {appName} combines the power of AI with a blazing-fast editor to help developers
          write better code, faster. Built for the modern developer workflow.
        </p>

        <div className="hero__actions">
          <button className="hero__btn hero__btn--primary" onClick={scrollToFeatures}>
            <span>Explore Features</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          <a href={docsUrl} target="_blank" rel="noreferrer" className="hero__btn hero__btn--outline">
            Read Documentation
          </a>
        </div>

        <div className="hero__stats">
          <div className="hero__stat">
            <span className="hero__stat-value">50+</span>
            <span className="hero__stat-label">AI Features</span>
          </div>
          <div className="hero__stat-divider"></div>
          <div className="hero__stat">
            <span className="hero__stat-value">30+</span>
            <span className="hero__stat-label">Languages</span>
          </div>
          <div className="hero__stat-divider"></div>
          <div className="hero__stat">
            <span className="hero__stat-value">10x</span>
            <span className="hero__stat-label">Faster Coding</span>
          </div>
          <div className="hero__stat-divider"></div>
          <div className="hero__stat">
            <span className="hero__stat-value">99.9%</span>
            <span className="hero__stat-label">Uptime</span>
          </div>
        </div>
      </div>

      <div className="hero__code-window">
        <div className="hero__window-bar">
          <div className="hero__window-dots">
            <span className="dot dot--red"></span>
            <span className="dot dot--yellow"></span>
            <span className="dot dot--green"></span>
          </div>
          <span className="hero__window-title">ultracoder.js</span>
        </div>
        <div className="hero__code">
          <pre><code>
<span className="code-comment">// 🤖 UltraCoder AI Assistant</span>
<span className="code-keyword">import</span> <span className="code-bracket">{'{'}</span> <span className="code-var">UltraCoder</span> <span className="code-bracket">{'}'}</span> <span className="code-keyword">from</span> <span className="code-string">'@ultracoder/core'</span>;

<span className="code-keyword">const</span> <span className="code-var">ai</span> = <span className="code-keyword">new</span> <span className="code-fn">UltraCoder</span><span className="code-bracket">()</span>;

<span className="code-comment">// Generate code with AI</span>
<span className="code-keyword">const</span> <span className="code-var">result</span> = <span className="code-keyword">await</span> <span className="code-var">ai</span>.<span className="code-fn">generate</span><span className="code-bracket">(</span><span className="code-bracket">{'{'}</span>
  <span className="code-prop">prompt</span>: <span className="code-string">"Build a REST API"</span>,
  <span className="code-prop">language</span>: <span className="code-string">"TypeScript"</span>,
  <span className="code-prop">optimize</span>: <span className="code-bool">true</span>
<span className="code-bracket">{'}'}</span><span className="code-bracket">)</span>;

<span className="code-comment">// Auto-review & improve</span>
<span className="code-var">ai</span>.<span className="code-fn">review</span><span className="code-bracket">(</span><span className="code-var">result</span>.<span className="code-prop">code</span><span className="code-bracket">)</span>;
          </code></pre>
          <div className="hero__cursor"></div>
        </div>
      </div>

      <div className="hero__scroll-hint">
        <div className="hero__scroll-mouse">
          <div className="hero__scroll-wheel"></div>
        </div>
        <span>Scroll to explore</span>
      </div>
    </section>
  );
};

export default Hero;